import React, { useContext, useEffect, useState } from 'react';
import { MedicationsSummary } from '@communicare/medications/MedicationSummary/MedicationsSummary';
import { ScrollMode } from '@communicare/medications/MedicationSummary/MedicationSummaryDataTable';
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import { ErrorBoundary } from "../common/ErrorBoundary";
import styles from './MountCommunicare.module.scss';
import { AuthContext } from "../auth/AuthContext";
import { useHistory } from 'react-router-dom';
import { DraftMedicationDialogBox } from '../common/dialogbox/DraftMedicationDialogBox';
import { PharmacyRequestResponse } from '@communicare/medications/PharmacyRequests/PharmacyRequests';
import GlobalSnackbar from '../common/snackbar/GlobalSnackbar';
import { FinalisedSucessMessage } from '../common/snackbar/FinalisedSucessMessage';
import { FinalisedFailMessage } from '../common/snackbar/FinalisedFailMessage';
import { RenderIf } from '../common/render-if/RenderIf';
import { CommonApi } from '../../services/Common';
import { IdentityContext } from '../auth/UserIdentity';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { setUpAppInsights } from '../../AppInsights';

export interface IMountCommunicareProps {
    patient: {
        authoringSystem: string;
        authoringSystemForeignKey: string;
        isOnboarding?: boolean;
        isDoctorView?: boolean;
    },
    encounter: {
        authoringSystem: string;
        authoringSystemForeignKey: string;
    },
    navigateToPharmacyRequest: () => void;
    hasDraftMedications?(flag: boolean): void;
}

export const CommunicareWrapper = (props: { children: JSX.Element; isPopUp?: boolean }) => {

    let style = props.isPopUp ? `${styles.communicareZIndex} ${styles.fixed}` : styles.communicareZIndex;
    return (
        <ErrorBoundary>
            <MuiPickersUtilsProvider utils={MomentUtils}>

                <div className={style}>
                    {props.children}
                </div>

            </MuiPickersUtilsProvider>
        </ErrorBoundary>
    );
};

const noop = () => { };
export function MountCommunicare(props: IMountCommunicareProps) {
    let { patient, encounter } = props;
    let auth = useContext(AuthContext);
    let saveUrl = auth?.saveCurrentPath || noop;
    const [hasDraftMedications, setHasDraftMedications] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    const history = useHistory();
    const [randomKey, setRandomKey] = useState('');
    const [open, setOpen] = React.useState(false);
    const [status, setStatus] = React.useState('');
    let ctx = useContext(IdentityContext);
    
    const onDraftMedicationsChanged = (draftMedications: any[]) => {

        let hasDrafts = (draftMedications.length > 0 && !patient.isOnboarding) ? true : false;

        setHasDraftMedications(hasDrafts);
        if (props.hasDraftMedications !== undefined)
            props.hasDraftMedications(hasDrafts)
    }

    //Prompt dailog is triggering only once and not triggering if user clicks on cancel. So, passing random value to retrigger 
    const createKey = () => {
        return Date.now() + Math.random().toString(36).substring(2, 9);
    }

    //Below block is to show browser confirmation prompt while user clicks on browser back button
    useEffect(() => {
        const onBackButtonEvent = (e: any) => {
            e.preventDefault();
            if (hasDraftMedications) {
                setShowPrompt(true);
            }
            setRandomKey(createKey);
        }

        if (hasDraftMedications) {
            history.push(window.location.pathname);
            window.addEventListener('popstate', onBackButtonEvent);
            return () => {
                window.removeEventListener('popstate', onBackButtonEvent);
            };
        }
    }, [hasDraftMedications, randomKey]);

    const logError = (error: PharmacyRequestResponse) => {
        const commonApi = new CommonApi();
        let errorDetails = {
            url: window.location.href,
            userName: ctx?.userInfo?.email,
            authoringSystem: patient.authoringSystem,
            authoringSystemForeignKey: patient.authoringSystemForeignKey,
            errorType: error.type,
            message: error.message,            
            errorPayload: error.errorPayload
        }
        commonApi.saveEventErrorLog('PharmacyRequest_Failed', JSON.stringify(errorDetails), error.message, patient.authoringSystem, patient.authoringSystemForeignKey);        
        setUpAppInsights().trackException({ exception: new Error('PharmacyRequest_Failed'), severityLevel: SeverityLevel.Error, }, errorDetails);
    }

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <React.Fragment>
            <DraftMedicationDialogBox
                when={showPrompt}
                onOK={() => true}
                onCancel={() => false}
                key={randomKey}
            />

            <RenderIf if={status == "success"}>
                <GlobalSnackbar message={<FinalisedSucessMessage onClose={handleClose} />} open={open} />
            </RenderIf>
            <RenderIf if={status == "fail"}>
                <GlobalSnackbar message={<FinalisedFailMessage onClose={handleClose} />} open={open} />
            </RenderIf>

            <CommunicareWrapper>
                <MedicationsSummary
                    encounter={{ ...encounter }}
                    patient={{ ...patient }}
                    service={{ ...encounter }}
                    setUrlForControlledMedicationReload={saveUrl}
                    getDefaultViewingRight={() => null}
                    scrollMode={ScrollMode.normal}
                    onDraftMedicationsChanged={onDraftMedicationsChanged}
                    headlessPharmacyRequest={{
                        onSuccess(response: PharmacyRequestResponse) {
                            setOpen(true);
                            setStatus('success');
                        },
                        onFailure(response: PharmacyRequestResponse) {
                            setOpen(true);
                            setStatus('fail');
                            logError(response);                           
                        }
                    }}
                />
            </CommunicareWrapper>
        </React.Fragment>
    );
}